import { Icon } from '@iconify/react';
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import ReactLoading from 'react-loading';
import { Schema } from '../../../../../amplify/data/resource';
import { FundLogo } from '../../../../components/custom/FundLogo';
import StatusTextCircle from '../../../../components/custom/StatusTextCircle';
import CardIcon from '../../../../components/icons/CardIcon';
import CoinIcon from '../../../../components/icons/CoinIcon';
import { formatMoney } from '../../../../utils/format/format';

const sidebarVariants = {
  hidden: { x: '-100%', transition: { duration: 0.5 } },
  visible: { x: 0, transition: { duration: 0.5 } },
};

const paymentSelectionSet = [
  'id',
  'link',
  'validUntil',
  'createdAt',
  'sentBy',
  'clientBank',
  'fund',
  'clientBranch',
  'clientAccount',
  'clientFullName',
  'clientId',
  'fee',
  'indexLogo',
  'paymentAmount',
  'polisaNumber',
  'description',
  'product',
  'user.fullName',
];

// @ts-ignore
type Payment = SelectionSet<
  // @ts-ignore
  Schema['AgentPaymentLink']['type'],
  typeof paymentSelectionSet
>;

const apiClient = generateClient<Schema>();

const Sidebar = ({
  open,
  toggleSidebar,
  paymentId,
}: {
  open: boolean;
  toggleSidebar: () => void;
  paymentId: string;
}) => {
  const theme = useTheme();

  const [payment, setPayment] = useState<Payment | null>(null);

  useEffect(() => {
    const fetchPayment = async () => {
      if (paymentId) {
        try {
          const response = await apiClient.models.AgentPaymentLink.get(
            { id: paymentId },
            {
              selectionSet: [
                'id',
                'link',
                'validUntil',
                'createdAt',
                'sentBy',
                'clientBank',
                'clientBranch',
                'clientAccount',
                'clientFullName',
                'clientId',
                'fee',
                'indexLogo',
                'fund',
                'paymentAmount',
                'polisaNumber',
                'description',
                'product',
                'user.fullName',
              ],
            },
          );

          setPayment(response.data as Payment | null);
        } catch (error) {
          console.error('Error fetching file:', error);
        } finally {
        }
      }
    };

    fetchPayment();
  }, [paymentId]);

  const [isClosing, setIsClosing] = useState(false);

  const sidebarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        toggleSidebar();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <motion.div
      ref={sidebarRef}
      initial="hidden"
      animate={open && !isClosing ? 'visible' : 'hidden'}
      exit="hidden"
      variants={sidebarVariants}
      style={{
        width: '500px',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: '#FFF',
        zIndex: 1200,
      }}
    >
      {payment ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            py: '45px',
            px: '25px',
            display: 'flex',
            flexDirection: 'column',
            gap: '48px',
            overflowY: 'auto',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              boxShadow: '0px 0px 50px 0px #00000040',
              flexDirection: 'column',
              gap: '20px',
              py: '20px',
              px: '30px',
              borderRadius: '30px',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Icon
                style={{ cursor: 'pointer' }}
                icon={'material-symbols:close'}
                onClick={toggleSidebar}
              />
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ width: '100%' }}
            >
              <Stack
                sx={{
                  background: '#F7F8FA',
                  borderRadius: '10px',
                  px: '16px',
                  py: '14px',
                }}
              >
                <Typography
                  fontWeight={'400'}
                  fontSize={'11px'}
                  fontFamily={'Montserrat'}
                >
                  {payment?.createdAt
                    ? new Date(payment.createdAt).toLocaleDateString('he-IL', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })
                    : ''}
                </Typography>
                <Typography
                  fontWeight={'500'}
                  fontSize={'11px'}
                  fontFamily={'Montserrat'}
                >
                  {payment?.createdAt
                    ? new Date(payment.createdAt).toLocaleTimeString('he-IL', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : ''}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                sx={{
                  background: '#F7F8FA',
                  borderRadius: '10px',
                  px: '16px',
                  width: '120px',
                  py: '19px',
                }}
              >
                <StatusTextCircle status={payment.status || 'PENDING'} />
              </Stack>
              <Stack spacing={1} flexShrink={0}>
                <Typography
                  fontWeight={'700'}
                  fontSize={'12px'}
                  fontFamily={'AlmoniDL'}
                >
                  שם סוכן:
                </Typography>
                <Typography
                  fontWeight={'700'}
                  fontSize={'12px'}
                  fontFamily={'AlmoniDL'}
                >
                  {payment?.user?.fullName}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <FundDetails payment={payment} />
          <ClientDetails payment={payment} />
        </Box>
      ) : (
        <Stack justifyContent="center" alignItems="center" width="100%">
          <ReactLoading
            type={'bubbles'}
            color={theme.palette.green.main}
            height={250}
            width={250}
          />
        </Stack>
      )}
    </motion.div>
  );
};

export default Sidebar;

const FundDetails = ({ payment }: { payment: Payment | null }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        boxShadow: '0px 0px 50px 0px #00000040',
        flexDirection: 'column',
        gap: '20px',
        py: '20px',
        px: '30px',
        borderRadius: '30px',
      }}
    >
      <Grid container spacing={2} direction="row-reverse">
        <Grid item xs={12} sm={6}>
          <Stack
            direction="row-reverse"
            gap={1}
            justifyContent="flex-end"
            alignItems="center"
            sx={{
              background: '#F2F4F7',
              borderRadius: '11px',
              py: '10px',
              px: '20px',
              height: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'29px'}
              fontFamily={'Montserrat'}
            >
              {formatMoney(payment?.paymentAmount || 0)}
            </Typography>
            <CoinIcon />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack
            justifyContent="space-between"
            sx={{
              background: '#F2F4F7',
              borderRadius: '11px',
              py: '10px',
              px: '20px',
              height: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'17px'}
              fontFamily={'AlmoniDL'}
            >
              {payment?.product}
            </Typography>
            <FundLogo width="100px" logoIndex={payment?.indexLogo || ''} />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack
            sx={{
              background: '#F2F4F7',
              borderRadius: '11px',
              py: '10px',
              px: '20px',
              height: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              fontWeight={'600'}
              fontSize={'8px'}
              fontFamily={'Montserrat'}
            >
              456772 . 600 . 12
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack
            sx={{
              background: '#F2F4F7',
              borderRadius: '11px',
              py: '10px',
              px: '20px',
              height: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'10px'}
              fontFamily={'AlmoniDL'}
            >
              נשלח למייל אצל היצרן:
            </Typography>
            <Typography
              fontWeight={'600'}
              fontSize={'9px'}
              fontFamily={'Montserrat'}
            >
              danameshulam@email.com
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

const ClientDetails = ({ payment }: { payment: Payment | null }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        boxShadow: '0px 0px 50px 0px #00000040',
        flexDirection: 'column',
        gap: '20px',
        py: '20px',
        px: '30px',
        borderRadius: '30px',
      }}
    >
      <Stack>
        <Stack direction="row-reverse" justifyContent="space-between">
          <Stack spacing={3}>
            <Stack
              spacing={1}
              sx={{
                background: '#F7F8FA',
                borderRadius: '10px',
                px: '20px',
                py: '10px',
              }}
            >
              <Typography
                fontWeight={'700'}
                fontSize={'12px'}
                fontFamily={'AlmoniDL'}
              >
                :נשלח בעזרת
              </Typography>
              <Stack gap={1} direction="row" alignItems="center">
                <Icon icon="ic:baseline-whatsapp" />
                <Typography
                  fontWeight={'700'}
                  fontSize={'15px'}
                  fontFamily={'AlmoniDL'}
                >
                  ווטסאפ
                </Typography>
              </Stack>
            </Stack>
            <Stack
              spacing={2}
              sx={{
                background: '#9EA8B5',
                borderRadius: '10px',
                px: '20px',
                py: '10px',
              }}
            >
              <Stack direction="row" gap={1} alignItems="center">
                <Icon color="#FFF" icon="hugeicons:mail-02" />
                <Typography
                  fontWeight={'400'}
                  fontSize={'12px'}
                  fontFamily={'Montserrat'}
                  textAlign="right"
                  color="#FFF"
                >
                  Moshe@email.com
                </Typography>
              </Stack>
              <Stack direction="row" gap={1} alignItems="center">
                <Icon color="#FFF" icon="akar-icons:phone" />
                <Typography
                  fontWeight={'400'}
                  fontSize={'12px'}
                  fontFamily={'Montserrat'}
                  textAlign="right"
                  color="#FFF"
                >
                  0548300730
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack spacing={2}>
            <CardIcon />
            <Stack>
              <Typography
                fontWeight={'700'}
                fontSize={'22px'}
                fontFamily={'AlmoniDL'}
              >
                {payment?.clientFullName}
              </Typography>
              <Typography
                fontWeight={'700'}
                fontSize={'20px'}
                fontFamily={'AlmoniDL'}
              >
                {payment?.clientId}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};
